import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from "../base";
import { CreateObject } from "../item";

import { Ticketbai } from './ticketbai';
import { Ticketsii } from './ticketsii';
import { Verifactu } from './verifactu';
import { Ticket } from './ticket';

/**
 * F: First change
 * P: Price changed
 * I: Invoice created
 * D: Destination change
 * C: Cancelled
 */
export type TicketChangeReason = 'F' | 'P' | 'I' | 'D' | 'C';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { TicketType } from '../types';
import { TicketChangeType } from '../types';
import { TicketbaiType } from '../types';
import { TicketsiiType } from '../types';
import { VerifactuType } from '../types';
import { SessionType } from '../types';
import { ClientType } from '../types';

const _schema = {
    "name": "TICKETCHANGE",
    "fields": [
        { name: "created", type: "date", volatile: true },
        { name: "updated", type: "date", volatile: true },
        { name: "ticket", type: "objid" },
        { name: "prev", type: "objid" },
        { name: "session", type: "objid" },
        { name: "client", type: "objid" },
        { name: "reason", type: "string" },
        { name: "series", type: "string" },
        { name: "invoice", type: "string" },
        { name: "total", type: "number" },
        { name: "base", type: "number" },
        { name: "taxes", type: "number" }
    ],
    relate: [
        { direction: ">", target: "TICKET", by: "ticket", name: "ticket", reason: "ticket", child: false, class: TicketType },
        { direction: ">", target: "TICKETCHANGE", by: "prev", name: "prev", reason: "prev", child: false, class: TicketChangeType },
        { direction: ">", target: "SESSION", by: "session", name: "session", reason: "session", child: false, class: SessionType },
        { direction: ">", target: "INVOICEBUSINESS", by: "client", name: "client", reason: "client", child: false, class: ClientType },
        { direction: "<", target: "VERIFACTU", by: "tckchg", name: "verifactu", reason: "verifactus", child: true, class: VerifactuType },
        { direction: "<", target: "TICKETBAI", by: "tckchg", name: "ticketbai", reason: "ticketbais", child: true, class: TicketbaiType },
        { direction: "<", target: "TICKETSII", by: "tckchg", name: "ticketsii", reason: "ticketsiis", child: true, class: TicketsiiType }
    ]
} as const;

const _TicketChangeClass = CreateObject(_schema);
if (!_TicketChangeClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class TicketChange extends _TicketChangeClass { 
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): TicketChange {
        return this._Copy(store) as TicketChange;
    }

    /** Changelog rows lack `TICKET.changes`; register on link (see TicketProduct). */
    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf || relation !== 'ticket' || !(related instanceof Ticket)){
            return;
        }

        if (related === this.ticket){
            related.AddChange(this);
        }
        else {
            related.DelChange(this);
        }
    }

    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    get verifactu() : Verifactu | null {
        if (this.verifactus.length > 0){
            return this.verifactus[0];
        }

        return null;
    }

    get ticketbai() : Ticketbai | null {
        if (this.ticketbais.length > 0){
            return this.ticketbais[0];
        }

        return null;
    }

    get ticketsii(): Ticketsii | null {
        if (this.ticketsiis.length > 0){
            return this.ticketsiis[0];
        }

        return null;
    }

    get ChangeReason(): TicketChangeReason | null{
        if (!this.ticket){
            return null;
        }

        if (this.ticket.IsCancelled){
            return 'C';
        }

        if (this.ticket.price && this.total){
            const rountprice = (Math.round(this.ticket.price) * 100)/100;
            const roundtotal = (Math.round(this.total) * 100)/100;

            if (rountprice != roundtotal){
                return 'P';
            }
        }

        const _invoice = this.ticket.invoice;
        if (_invoice){
            const _prev = this.client ?? null;
            const _next = _invoice.client ?? null;

            if (_prev !== _next){
                return _prev !== null ? 'D' : 'I';
            }
        }

        return null;
    }
}